import { Loader2 } from "lucide-react";
import type { ButtonHTMLAttributes, ReactNode } from "react";

import { cn } from "../../lib/utils";

type Variant = "primary" | "secondary" | "ghost" | "danger";
type Size = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: Variant;
  readonly size?: Size;
  readonly loading?: boolean;
  readonly leftIcon?: ReactNode;
  readonly rightIcon?: ReactNode;
}

const SIZE: Record<Size, string> = {
  sm: "h-8 px-3 text-xs",
  md: "h-10 px-4 text-sm",
  lg: "h-12 px-5 text-base",
};

const VARIANT: Record<Variant, string> = {
  primary:
    "bg-brand-primary text-brand-primary-fg shadow-sm hover:opacity-90 active:opacity-80 disabled:bg-surface-strong disabled:text-faint disabled:shadow-none",
  secondary:
    "bg-surface text-foreground ring-1 ring-inset ring-line hover:bg-surface-muted active:bg-surface-strong disabled:text-faint disabled:bg-surface-muted",
  ghost:
    "bg-transparent text-foreground hover:bg-surface-strong active:bg-surface-strong disabled:text-faint",
  danger:
    "bg-red-600 text-white shadow-sm hover:bg-red-700 active:bg-red-800 disabled:bg-surface-strong disabled:text-faint",
};

/**
 * Primary action component. Variants cover the four roles a button
 * plays in the POS surface - primary action, secondary action,
 * tertiary/icon action, destructive action. Loading state collapses
 * the leftIcon into a spinner so width doesn't jump.
 */
export function Button({
  variant = "primary",
  size = "md",
  loading = false,
  leftIcon,
  rightIcon,
  className,
  children,
  disabled,
  type = "button",
  ...rest
}: ButtonProps) {
  const isDisabled = disabled || loading;
  return (
    <button
      type={type}
      disabled={isDisabled}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-md font-semibold transition-[opacity,background-color,box-shadow]",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary",
        "disabled:cursor-not-allowed",
        SIZE[size],
        VARIANT[variant],
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : leftIcon}
      {children}
      {!loading && rightIcon}
    </button>
  );
}
